import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { safetyColors, safetyType } from "../ui/tokens";
import { BadgeErrorBoundary } from "./BadgeErrorBoundary";
import {
  BadgeDocument,
  BadgeResult,
  BadgeView,
  fetchBadge,
  tokenFromPathname,
} from "./badgeClient";
import { MelaChip } from "@/ui";
import { safetyPillPalette } from "../ui/StatusBadge";
import { moduleShellTokens } from "@/layout/module-shell/module-shell.tokens";

type Tone = "ok" | "warning" | "danger";

const tonePalette = (tone: Tone) =>
  safetyPillPalette(
    safetyColors[`${tone}Bg` as const],
    safetyColors[tone],
    safetyColors[`${tone}Border` as const],
  );

const summaryTone: Record<BadgeView["summary"], Tone> = {
  OK: "ok",
  WARNING: "warning",
  BLOCKED: "danger",
};

const stateTone: Record<BadgeDocument["state"], Tone> = {
  VALID: "ok",
  EXPIRING: "warning",
  EXPIRED: "danger",
  MISSING: "danger",
};

const isKnown = (map: Record<string, Tone>, value: string): boolean =>
  Object.prototype.hasOwnProperty.call(map, value);

/*
  Backend deploys before web, so a server enum this build has never heard of is
  the expected state during a rollout. Interpolating it would print a raw i18n
  key at a stranger; degrade to the most alarming value instead, so a rollout
  can never quietly downgrade a warning.
*/
const knownSummary = (value: BadgeView["summary"]): BadgeView["summary"] =>
  isKnown(summaryTone, value) ? value : "BLOCKED";

const knownState = (value: BadgeDocument["state"]): BadgeDocument["state"] =>
  isKnown(stateTone, value) ? value : "MISSING";

const BADGE_LAYOUT_CSS = `
.sbp-shell { height: 100vh; height: 100dvh; padding: 24px 16px; }
.sbp-card { max-height: calc(100vh - 48px); max-height: calc(100dvh - 48px); }
.sbp-identity { padding: 24px 24px 8px; gap: 12px; }
.sbp-identity-flush { padding-bottom: 20px; }
.sbp-avatar { width: 96px; height: 96px; }
.sbp-scroll { overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
@media (max-height: 640px) {
  .sbp-shell { padding: 8px; }
  .sbp-card { max-height: calc(100vh - 16px); max-height: calc(100dvh - 16px); }
  .sbp-identity { padding: 12px 24px 4px; gap: 8px; }
  .sbp-identity-flush { padding-bottom: 12px; }
  .sbp-avatar { width: 56px; height: 56px; }
}
@media (max-height: 400px) {
  .sbp-shell { height: auto; min-height: 100vh; min-height: 100dvh; }
  .sbp-card { max-height: none; }
}
`;

const initialsOf = (first: string, last: string | null): string =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * `dd/mm/yyyy` in the VIEWER's timezone, like the authenticated screens. Not
 * the UTC day: an expiry is stored as local midnight of the AUTHOR's timezone,
 * so at a positive offset the UTC day is the one before the day that was typed.
 * A date-only value carries no instant to shift, so its parts are used as-is.
 * Unparseable input passes through untouched.
 */
export const formatBadgeDate = (iso: string): string => {
  const dateOnly = DATE_ONLY.exec(iso);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;

  return `${pad2(parsed.getDate())}/${pad2(parsed.getMonth() + 1)}/${parsed.getFullYear()}`;
};

const setMeta = (name: string, content: string): (() => void) => {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }
  const prev = meta.content;
  meta.content = content;
  return () => {
    meta!.content = prev;
  };
};

const useNoIndex = (title: string, lang: string) => {
  useEffect(() => {
    document.title = title;
    if (lang) document.documentElement.lang = lang;
    const restoreRobots = setMeta("robots", "noindex, nofollow");
    // The page URL carries the badge token, so it must never leave in a
    // Referer header (the API sets the same policy on its own responses).
    const restoreReferrer = setMeta("referrer", "no-referrer");
    return () => {
      restoreRobots();
      restoreReferrer();
    };
  }, [title, lang]);
};

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="sbp-shell"
    style={{
      flex: "1 1 auto",
      minWidth: 0,
      display: "flex",
      position: "relative",
      boxSizing: "border-box",
      backgroundColor: safetyColors.surface,
    }}
  >
    <style>{BADGE_LAYOUT_CSS}</style>
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: safetyColors.surface,
        zIndex: 0,
      }}
    />
    <div
      className="sbp-card"
      style={{
        position: "relative",
        zIndex: 1,
        margin: "auto",
        width: "100%",
        maxWidth: 460,
        display: "flex",
        flexDirection: "column",
        backgroundColor: safetyColors.white,
        borderRadius: moduleShellTokens.surfaceRadius,
        border: moduleShellTokens.surfaceBorder,
        boxShadow: moduleShellTokens.surfaceShadow,
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  </div>
);

const CenteredMessage: React.FC<{
  emoji: string;
  title: string;
  body: string;
  onRetry?: { label: string; run: () => void };
}> = ({ emoji, title, body, onRetry }) => (
  <div style={{ padding: "48px 28px", textAlign: "center" }}>
    <div style={{ fontSize: 40, marginBottom: 12 }}>{emoji}</div>
    <div
      style={{
        fontSize: safetyType.title,
        fontWeight: 700,
        color: safetyColors.brandNavy,
        marginBottom: 8,
        overflowWrap: "anywhere",
      }}
    >
      {title}
    </div>
    <div
      style={{
        fontSize: safetyType.body,
        color: safetyColors.textMid,
        overflowWrap: "anywhere",
      }}
    >
      {body}
    </div>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry.run}
        style={{
          marginTop: 20,
          padding: "8px 18px",
          borderRadius: 8,
          border: `1px solid ${safetyColors.border}`,
          backgroundColor: safetyColors.white,
          color: safetyColors.brandNavy,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {onRetry.label}
      </button>
    )}
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div
    style={{
      fontSize: safetyType.caption,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: safetyColors.muted,
      marginTop: 12,
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

const listStyle: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
};

const DocumentRow: React.FC<{ doc: BadgeDocument; t: TFunc }> = ({
  doc,
  t,
}) => {
  const state = knownState(doc.state);
  const tone = stateTone[state];
  const detail = (() => {
    if (state === "EXPIRED") {
      return doc.expiryDate
        ? t("safety.badge.public.doc.expiredOn.translation", {
            date: formatBadgeDate(doc.expiryDate),
          })
        : t("safety.badge.public.doc.expired.translation");
    }
    if (state === "EXPIRING") {
      return doc.expiryDate
        ? t("safety.badge.public.doc.expiresOn.translation", {
            date: formatBadgeDate(doc.expiryDate),
          })
        : t("safety.badge.public.doc.expiring.translation");
    }
    if (state === "MISSING") {
      return t("safety.badge.public.doc.missing.translation");
    }
    return doc.expiryDate
      ? t("safety.badge.public.doc.validUntil.translation", {
          date: formatBadgeDate(doc.expiryDate),
        })
      : "";
  })();

  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 0",
        borderTop: `1px solid ${safetyColors.borderLight}`,
      }}
    >
      {/*
        `overflowWrap: "anywhere"` on server data: titles are usually filenames
        ("DVR_Cantiere_ViaRoma12_rev3.pdf") and an underscore is not a wrapping
        opportunity, so an unbroken line would widen the scroll region.
      */}
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: safetyType.body,
            fontWeight: 600,
            color: safetyColors.textStrong,
            overflowWrap: "anywhere",
          }}
        >
          {doc.categoryLabel}
        </div>
        {doc.title && doc.title.trim() !== "" && (
          <div
            style={{
              fontSize: safetyType.caption,
              color: safetyColors.textMid,
              marginTop: 2,
              overflowWrap: "anywhere",
            }}
          >
            {doc.title}
          </div>
        )}
        {detail && (
          <div
            style={{
              fontSize: safetyType.caption,
              color: safetyColors.textMid,
              marginTop: 2,
              overflowWrap: "anywhere",
            }}
          >
            {detail}
          </div>
        )}
        {doc.attachmentMissing && (
          <div
            style={{
              fontSize: safetyType.caption,
              color: safetyColors.danger,
              marginTop: 2,
            }}
          >
            {t("safety.badge.public.doc.attachmentMissing.translation")}
          </div>
        )}
      </div>
      <MelaChip palette={tonePalette(tone)} style={{ flexShrink: 0 }}>
        {t(`safety.badge.public.doc.state.${state.toLowerCase()}.translation`)}
      </MelaChip>
    </li>
  );
};

/** Name only — the payload deliberately carries no task ids. */
const TaskRow: React.FC<{ name: string }> = ({ name }) => (
  <li
    style={{
      padding: "10px 0",
      borderTop: `1px solid ${safetyColors.borderLight}`,
      fontSize: safetyType.body,
      fontWeight: 600,
      color: safetyColors.textStrong,
      overflowWrap: "anywhere",
    }}
  >
    {name}
  </li>
);

type TFunc = (key: string, options?: Record<string, unknown>) => string;

const BadgeCard: React.FC<{ view: BadgeView; t: TFunc }> = ({ view, t }) => {
  const [photoFailed, setPhotoFailed] = useState(false);
  // Anchor the countdown to an absolute expiry so a throttled or backgrounded
  // tab can't keep showing an expired token as still valid (a decremented
  // snapshot drifts while the tab is suspended).
  const [expiresAt] = useState(() => Date.now() + view.expiresInSeconds * 1000);
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)),
  );
  const { worker, documents } = view;
  const summary = knownSummary(view.summary);
  // Optional on the wire: a payload from a backend without the field yet.
  const tasks = view.tasks ?? [];
  const hasSections = documents.length > 0 || tasks.length > 0;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  useEffect(() => {
    const recompute = () =>
      setSecondsLeft(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
    recompute();
    const id = window.setInterval(recompute, 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") recompute();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [expiresAt]);

  const syncScrollAffordance = useCallback(() => {
    const el = scrollRef.current;
    setHasMoreBelow(
      !!el && el.scrollHeight - el.scrollTop - el.clientHeight > 1,
    );
  }, []);

  useEffect(() => {
    syncScrollAffordance();
    const scroller = scrollRef.current;
    if (!scroller) return;

    window.addEventListener("resize", syncScrollAffordance);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(syncScrollAffordance);
    observer?.observe(scroller);
    if (contentRef.current) observer?.observe(contentRef.current);

    return () => {
      window.removeEventListener("resize", syncScrollAffordance);
      observer?.disconnect();
    };
  }, [syncScrollAffordance, hasSections, documents.length, tasks.length]);

  const fullName = [worker.firstName, worker.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div
        style={{
          flexShrink: 0,
          backgroundColor: safetyColors.brandNavy,
          color: safetyColors.white,
          padding: "20px 24px",
          textAlign: "center",
          fontSize: safetyType.bodyStrong,
          fontWeight: 600,
          letterSpacing: 0.3,
        }}
      >
        {t("safety.badge.public.title.translation")}
      </div>

      <div
        className={
          hasSections ? "sbp-identity" : "sbp-identity sbp-identity-flush"
        }
        style={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {worker.photoUrl && !photoFailed ? (
          <img
            className="sbp-avatar"
            src={worker.photoUrl}
            alt=""
            onError={() => setPhotoFailed(true)}
            style={{
              borderRadius: 0,
              objectFit: "cover",
              border: `2px solid ${safetyColors.border}`,
            }}
          />
        ) : (
          <div
            className="sbp-avatar"
            style={{
              borderRadius: 0,
              backgroundColor: safetyColors.brandNavy,
              color: safetyColors.white,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: safetyType.heading,
              fontWeight: 700,
            }}
          >
            {initialsOf(worker.firstName, worker.lastName)}
          </div>
        )}

        <div
          style={{
            fontSize: safetyType.heading,
            fontWeight: 700,
            color: safetyColors.brandNavy,
            textAlign: "center",
          }}
        >
          {fullName || "—"}
        </div>

        {worker.taxCode && (
          <div
            style={{
              fontSize: safetyType.body,
              color: safetyColors.textMid,
            }}
          >
            <span style={{ color: safetyColors.muted }}>
              {t("safety.badge.public.taxCode.translation")}
            </span>{" "}
            <span style={{ fontWeight: 600, color: safetyColors.textStrong }}>
              {worker.taxCode}
            </span>
          </div>
        )}

        <MelaChip size="md" palette={tonePalette(summaryTone[summary])}>
          {t(
            `safety.badge.public.summary.${summary.toLowerCase()}.translation`,
          )}
        </MelaChip>
      </div>

      {hasSections && (
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <div
            ref={scrollRef}
            className="sbp-scroll"
            data-testid="badge-scroll"
            onScroll={syncScrollAffordance}
            tabIndex={0}
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              padding: "8px 24px 20px",
            }}
          >
            <div ref={contentRef}>
              {documents.length > 0 && (
                <div data-testid="badge-section-documents">
                  <SectionTitle>
                    {t("safety.badge.public.documents.title.translation")}
                  </SectionTitle>
                  <ul style={listStyle}>
                    {documents.map((doc, i) => (
                      <DocumentRow
                        key={`${doc.categoryLabel}-${i}`}
                        doc={doc}
                        t={t}
                      />
                    ))}
                  </ul>
                </div>
              )}

              {tasks.length > 0 && (
                <div data-testid="badge-section-tasks">
                  <SectionTitle>
                    {t("safety.badge.public.tasks.title.translation")}
                  </SectionTitle>
                  <ul style={listStyle}>
                    {tasks.map((task, i) => (
                      <TaskRow key={`${task.name}-${i}`} name={task.name} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {hasMoreBelow && (
            <div
              aria-hidden="true"
              data-testid="badge-scroll-fade"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 28,
                pointerEvents: "none",
                // Not `transparent`: Safari interpolates it through black.
                background: `linear-gradient(to bottom, rgba(255,255,255,0), ${safetyColors.white})`,
              }}
            />
          )}
        </div>
      )}

      <div
        style={{
          flexShrink: 0,
          padding: "12px 24px",
          borderTop: `1px solid ${safetyColors.borderLight}`,
          textAlign: "center",
          fontSize: safetyType.micro,
          color: safetyColors.muted,
        }}
      >
        {secondsLeft > 0
          ? t("safety.badge.public.validFor.translation", {
              seconds: secondsLeft,
            })
          : t("safety.badge.public.stale.translation")}
      </div>
    </>
  );
};

const ErrorCard: React.FC<{
  t: TFunc;
  emoji: string;
  messageKey: string;
  onRetry?: { label: string; run: () => void };
}> = ({ t, emoji, messageKey, onRetry }) => (
  <Shell>
    <CenteredMessage
      emoji={emoji}
      title={t(`safety.badge.public.error.${messageKey}.title.translation`)}
      body={t(`safety.badge.public.error.${messageKey}.body.translation`)}
      onRetry={onRetry}
    />
  </Shell>
);

const BadgePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [token] = useState(() => tokenFromPathname(window.location.pathname));
  const [result, setResult] = useState<BadgeResult | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const lng = i18n.language?.split("-")[0];

  useEffect(() => {
    let alive = true;
    if (!token) {
      setResult({ status: "invalid" });
      return;
    }
    setResult(null);
    fetchBadge(token, lng).then((r) => {
      if (alive) setResult(r);
    });
    return () => {
      alive = false;
    };
  }, [token, lng, reloadKey]);

  const retry = {
    label: t("safety.badge.public.retry.translation"),
    run: () => setReloadKey((k) => k + 1),
  };

  if (result === null) {
    return (
      <Shell>
        <CenteredMessage
          emoji="⏳"
          title={t("safety.badge.public.loading.translation")}
          body=""
        />
      </Shell>
    );
  }

  if (result.status === "ok") {
    return (
      <Shell>
        <BadgeCard view={result.view} t={t} />
      </Shell>
    );
  }

  const errors: Record<
    Exclude<BadgeResult["status"], "ok">,
    { emoji: string; retry: boolean }
  > = {
    expired: { emoji: "⌛", retry: false },
    invalid: { emoji: "🔍", retry: false },
    rate_limited: { emoji: "🚦", retry: true },
    error: { emoji: "⚠️", retry: true },
  };
  const key =
    result.status === "rate_limited"
      ? "rateLimited"
      : result.status === "error"
        ? "network"
        : result.status;
  const cfg = errors[result.status];

  return (
    <ErrorCard
      t={t}
      emoji={cfg.emoji}
      messageKey={key}
      onRetry={cfg.retry ? retry : undefined}
    />
  );
};

export const SafetyBadgePublicPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  useNoIndex(
    t("safety.badge.public.title.translation"),
    i18n.language?.split("-")[0] ?? "",
  );

  return (
    <BadgeErrorBoundary
      fallback={
        <ErrorCard
          t={t}
          emoji="⚠️"
          messageKey="network"
          onRetry={{
            label: t("safety.badge.public.retry.translation"),
            run: () => window.location.reload(),
          }}
        />
      }
    >
      <BadgePage />
    </BadgeErrorBoundary>
  );
};

export default SafetyBadgePublicPage;
